import type { RefObject } from "react";
import { useRef, useState } from "react";

import { activationTracking } from "app/pages/activation/services/tracking";
import type { KeywordsGroupsSchema } from "app/pages/activation/hooks/useKeywordsGroups";

import { MAXIMUM_TOPICS } from "../constants";
import type { KWGroupSchema } from "../types";

import DropdownList from "./DropdownList/DropdownList";

type KeywordsDropdownSchema = {
    createdTopicsLength: number;
    isTopicsLoading: boolean;
    keywordsGroups: KeywordsGroupsSchema;
    dropdownRef: RefObject<any>;
    onAddTopics: (ids: string[]) => void;
};

const KeywordsDropdown = ({
    createdTopicsLength,
    isTopicsLoading,
    keywordsGroups,
    dropdownRef,
    onAddTopics,
}: KeywordsDropdownSchema) => {
    const track = activationTracking.home;

    const [selectedKWGroupIds, setSelectedKWGroupIds] = useState({});

    const isSomeItemSelected = useRef(false);
    const selectedKeywords = useRef([]);

    const isMaxSelected = selectedKeywords?.current?.length + createdTopicsLength >= MAXIMUM_TOPICS;
    const isMaxCreated = createdTopicsLength === MAXIMUM_TOPICS;

    const onSelect = (selectedItem: KWGroupSchema) => {
        setSelectedKWGroupIds((KWGroupIds) => {
            const currentItem = KWGroupIds[selectedItem.id];

            if (isMaxSelected && !currentItem) {
                return KWGroupIds;
            }

            const changedStateToBoolean = !currentItem;

            const newState = {
                ...KWGroupIds,
                [selectedItem.id]: changedStateToBoolean,
            };

            isSomeItemSelected.current = Object.values(newState).some((value: boolean) => value);

            if (changedStateToBoolean || currentItem === undefined) {
                selectedKeywords.current.push(selectedItem.id);

                track.trackLinkKWListSelect();
            } else {
                selectedKeywords.current = selectedKeywords.current.filter((id: string) => id !== selectedItem.id);

                track.trackLinkKWListUnselect();
            }

            return newState;
        });
    };

    const onApply = () => {
        const selectedIds = selectedKeywords.current;

        if (selectedIds.length) {
            onAddTopics(selectedIds);
            isSomeItemSelected.current = false;
            selectedKeywords.current = [];

            setSelectedKWGroupIds({});

            dropdownRef.current?.closePopup();
        }
    };

    if (isTopicsLoading || !keywordsGroups) {
        return null;
    }

    return (
        <DropdownList
            keywordsGroups={keywordsGroups}
            isMaxSelected={isMaxSelected}
            isMaxCreated={isMaxCreated}
            selectedKWGroupIds={selectedKWGroupIds}
            dropdownRef={dropdownRef}
            isTopicsLoading={isTopicsLoading}
            isSomeItemSelected={isSomeItemSelected?.current}
            onSelect={onSelect}
            onApply={onApply}
        />
    );
};

export default KeywordsDropdown;
